<template>
  <modal
    name="add-library-product-modal"
    width="60%"
    height="80%"
    :clickToClose="false"
    @before-open="beforeOpen"
    @before-close="beforeClose"
    :adaptive="true"
  >
    <div
      class="h-full transform overflow-auto rounded-lg bg-white shadow-xl transition-all sm:w-full"
    >
      <div class="flex items-center justify-between border-b border-gray-200 px-6 py-4">
        <h2 class="text-lg font-medium text-gray-900">Add Library Product</h2>
        <button
          @click="$modal.hide('add-library-product-modal')"
          type="button"
          class="focus:outline-none rounded-md bg-white text-gray-400 hover:text-gray-500"
        >
          <span class="sr-only">Close</span>
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div class="p-6">
        <products-table
          :products="products"
          :loading="loading"
          :rows="10"
          :currentFilter="'product'"
          :disableFilters="true"
          @edit-product="selectProduct"
          @grid-ready="onGridReady"
        />
      </div>
      
      <div class="flex justify-end space-x-3 border-t border-gray-200 px-6 py-4">
        <button
          @click="$modal.hide('add-library-product-modal')"
          type="button"
          class="focus:outline-none inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          @click="addSelectedProducts"
          :disabled="!selectedProducts.length || isLoading"
          :class="{ 'opacity-50 cursor-not-allowed': !selectedProducts.length || isLoading }"
          class="focus:outline-none inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
        >
          Add Selected Products
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapGetters } from 'vuex';
import ProductsTable from '@/components/ProductsTable.vue';

export default {
  name: 'AddLibraryProductModal',
  components: {
    ProductsTable
  },
  props: {
    posProvider: {
      type: String,
      default: ''
    },
    categories: {
      type: Array
    }
  },
  data() {
    return {
      loading: false,
      isLoading: false,
      products: [],
      selectedProducts: [],
      categoryId: '',
      gridApi: null
    };
  },
  computed: {
    ...mapGetters({
      currency: 'user/currencySettings',
      isFeatureAvailable: 'user/isFeatureAvailable',
      user: 'user/currentUser'
    })
  },
  methods: {
    beforeOpen(event) {
      this.categoryId = event.params.categoryId;
      this.fetchLibraryProducts();
    },
    beforeClose() {
      this.selectedProducts = [];
      this.products = [];
    },
    onGridReady(params) {
      this.gridApi = params.api;
      
      // Set up selection changed listener
      this.gridApi.addEventListener('selectionChanged', this.onSelectionChanged);
    },
    onSelectionChanged() {
      this.selectedProducts = this.gridApi.getSelectedRows();
    },
    selectProduct(product) {
      // Toggle selection when clicking on a product
      const selectedNodes = this.gridApi.getSelectedNodes();
      const isSelected = selectedNodes.some(node => node.data.id === product.id);
      
      if (isSelected) {
        this.gridApi.deselectNode(selectedNodes.find(node => node.data.id === product.id));
      } else {
        this.gridApi.selectNode(this.gridApi.getRowNode(product.id), true);
      }
    },
    async fetchLibraryProducts() {
      this.loading = true;
      
      try {
        // Use the same endpoint as Products.vue with type=product filter
        const { data } = await this.$axios.get('/menu-items', {
          params: {
            type: 'product'
          }
        });
        this.products = data.results;
      } catch (error) {
        this.$notify({
          group: 'settings',
          duration: 5000,
          text: 'Failed to load library products'
        });
      } finally {
        this.loading = false;
      }
    },
    async addSelectedProducts() {
      if (!this.selectedProducts.length) return;
      
      this.isLoading = true;
      
      try {
        // Add each selected product to the category
        for (const product of this.selectedProducts) {
          const productToAdd = {
            libraryItemId: product.id,
            categoryId: this.categoryId,
            menuId: this.$route.params.id
          };
          
          await this.$axios.post('/products/from-library', productToAdd);
        }
        
        this.$modal.hide('add-library-product-modal');
        this.$emit('product-added');
        
        this.$notify({
          group: 'settings',
          duration: 2000,
          text: `${this.selectedProducts.length} product(s) added`
        });
      } catch (error) {
        this.$notify({
          group: 'settings',
          duration: 5000,
          text: 'Failed to add products'
        });
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>
